// ----------------------------------------------------------------
// <copyright company='Microsoft Corporation'>
//     Copyright (c) Microsoft Corporation.  All rights reserved.
// </copyright>
// ----------------------------------------------------------------

import {
    call,
    put,
} from 'redux-saga/effects';

import {
    GET_REPAIR_OFFER,
    saveRepairOffer,
} from './../../../store/orderCreation';

import {
    actionStatusFailed,
    actionStatusStarted,
    actionStatusSuccess,
} from './../../../store/actionStatus';

import DeviceDal from './../../../dal/DeviceDal';

export function* getRepairOffer(request: any): IterableIterator<any> {
    try {
        const { additionalInfo } = request.payload;
        
        const accessoriesPayload = generateAccessoriesRequestPayload(request.payload);
        const partsPayload = generatePartsRequestPayload(request.payload);
        const devicePayload = generateDeviceRequestPayload(request.payload);
        const softwareOrderPayload = generateSoftwareOrderDeviceRequestPayload(request.payload);

        yield put(actionStatusStarted({actionName: GET_REPAIR_OFFER}));
        const softwareRepairOffer = yield call (DeviceDal.getSoftwareRepairOffer, softwareOrderPayload);

        const fullDeviceRepairOffer = yield call(
            DeviceDal.getFullDeviceRepairOffer, devicePayload);

        const accessoryAndPartRepairOffer = yield call(
            DeviceDal.getAccessoryAndPartRepairOffer, {
                additionalInfo,
                accessoriesPayload,
                partsPayload,
            });

        yield put(actionStatusSuccess({actionName: GET_REPAIR_OFFER}));
        yield put(
            saveRepairOffer({
                softwareRepairOffer,
                fullDeviceRepairOffer,
                accessoryAndPartRepairOffer,
            })
        );
    } catch (e) {
        yield put(actionStatusFailed({
            actionName: GET_REPAIR_OFFER,
            data: {},
            error: e,
        }));
    }
}

function generateAccessoriesRequestPayload(payload: any): any[] {
    const { repairDetails = {}, additionalInfo = {} } = payload;

    const accessories = repairDetails.accessories || {};
    return Object.keys(accessories).map((skuKey) => {
        const item = accessories[skuKey];

        return {
            AccessorySku: item.data.symptomCode,
            SerialNumber: additionalInfo.serialNumber,
        };
    }) || [];
}

function generatePartsRequestPayload(payload: any): any[] {
    const { repairDetails = {}, additionalInfo = {} } = payload;
    const parts = (repairDetails.isFullDeviceRepair || repairDetails.isSoftwareOrder) ? {} : repairDetails.deviceItems || {};
    return Object.keys(parts).map((skuKey) => {
        const item = parts[skuKey];
        return {
            PartCode: item.symptomCode,
            SerialNumber: additionalInfo.serialNumber,
            SymptomCode: item.problemType && item.problemType.symptomCode,
            OnDemandFru: item.onDemandFru,
            IsCruOffer : item.isCruOffer,
        };
    }) || [];
}

function generateSoftwareOrderDeviceRequestPayload(payload: any): any {
    const { repairDetails = {}, additionalInfo = {} } = payload;
    const deviceRepairInfo = (repairDetails.isSoftwareOrder) && repairDetails.deviceItems || {};
    return Object.values(deviceRepairInfo).reduce((deviceInfo, value: any) => {
        return {
            countryCode: additionalInfo.countryCode,
            postalCode: additionalInfo.postalCode,
            serialNumber: value.serialNumber,
            symptomCode: value.problemType.symptomCode,
            userAgent: additionalInfo.userAgent,
            soldToId: additionalInfo.soldToId,
            isSoftwareOrder: additionalInfo.isSoftwareOrder,
        };
    }, {});
}

function generateDeviceRequestPayload(payload: any): any {
    const { repairDetails = {}, additionalInfo = {} } = payload;
    const deviceRepairInfo = repairDetails.isFullDeviceRepair && repairDetails.deviceItems || {};
    return Object.values(deviceRepairInfo).reduce((deviceInfo, value: any) => {
        return {
            countryCode: additionalInfo.countryCode,
            postalCode: additionalInfo.postalCode,
            serialNumber: value.serialNumber,
            symptomCode: value.problemType.symptomCode,
            userAgent: additionalInfo.userAgent,
            soldToId: additionalInfo.soldToId,
        };
    }, {});
}
